import React, { useEffect, useState, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PreviewForm {
  form_number: string;
  form_name: string;
  is_mandatory: boolean;
}

interface PreviewTask {
  title: string;
  projected_date: string | null;
  due_date_offset_days: number | null;
  is_court_mandated: boolean;
  mandate_reference: string | null;
  priority: string;
  task_type: string | null;
  assignee_role: string | null;
  required_forms: PreviewForm[];
  estimated_cost: number | null;
  fee_description: string | null;
  subtask_count: number;
}

interface PreviewPhase {
  name: string;
  order: number;
  tasks: PreviewTask[];
}

interface PreviewData {
  template_name: string;
  template_id: string;
  lifecycle_status: string;
  confidence_score: string | null;
  estimated_duration_days: number | null;
  complexity_level: string | null;
  total_task_count: number;
  total_form_count: number;
  filing_fee_total: number;
  phases: PreviewPhase[];
  warnings: string[];
}

interface TaskTemplatePreviewPanelProps {
  templateId: string;
  jurisdictionCode?: string;
  triggerDate?: string;
  petitionFiledDate?: string;
  onClose: () => void;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function LifecycleBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    validated: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Validated' },
    draft: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Draft' },
    stale: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Stale' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function ConfidenceDot({ score }: { score: string | null }) {
  if (!score) return null;
  const colors: Record<string, string> = {
    high: 'bg-green-500',
    medium: 'bg-amber-500',
    low: 'bg-red-500',
  };
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <span className={`w-2 h-2 rounded-full ${colors[score] || 'bg-gray-400'}`} />
      {score.charAt(0).toUpperCase() + score.slice(1)} confidence
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
    </div>
  );
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-gray-500 dark:text-gray-400',
};

function formatDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

// ─── Main Component ─────────────────────────────────────────────────────────

const TaskTemplatePreviewPanel: React.FC<TaskTemplatePreviewPanelProps> = ({
  templateId,
  jurisdictionCode,
  triggerDate,
  petitionFiledDate,
  onClose,
}) => {
  const { keycloak } = useKeycloak();
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set([0]));
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = keycloak?.token;
    if (!token || !templateId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/v1/task-templates/preview-instantiate', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: templateId,
        trigger_date: triggerDate || new Date().toISOString().slice(0, 10),
        jurisdiction_code: jurisdictionCode,
        petition_filed_date: petitionFiledDate,
      }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(typeof err?.description === 'string' ? err.description : 'Failed to load preview');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [templateId, jurisdictionCode, triggerDate, petitionFiledDate, keycloak?.token]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 150); // match lex-panel-slide-out duration
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const togglePhase = (index: number) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Aggregate forms across all phases
  const allForms = data
    ? Array.from(
        new Map(
          data.phases
            .flatMap((p) => p.tasks.flatMap((t) => t.required_forms))
            .map((f) => [f.form_number, f]),
        ).values(),
      )
    : [];

  const mandatoryForms = allForms.filter((f) => f.is_mandatory);
  const optionalForms = allForms.filter((f) => !f.is_mandatory);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        className={`max-w-lg w-full bg-white dark:bg-gray-800 shadow-xl h-full overflow-y-auto ${
          closing ? 'lex-preview-panel-exit' : 'lex-preview-panel-enter'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {loading ? 'Loading preview...' : data?.template_name || 'Template Preview'}
              </h2>
              {data && (
                <div className="flex items-center gap-2 mt-1.5">
                  <LifecycleBadge status={data.lifecycle_status} />
                  <ConfidenceDot score={data.confidence_score} />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Calculating projected dates...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="m-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Content */}
        {data && !loading && (
          <div className="p-5 space-y-5">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Tasks" value={data.total_task_count} />
              <StatCard label="Forms" value={data.total_form_count} />
              <StatCard
                label="Duration"
                value={data.estimated_duration_days ? `${data.estimated_duration_days}d` : '--'}
              />
            </div>

            {/* Complexity + Fee Summary */}
            <div className="flex items-center justify-between text-sm">
              {data.complexity_level && (
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    data.complexity_level === 'simple'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : data.complexity_level === 'moderate'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                  }`}
                >
                  {data.complexity_level.charAt(0).toUpperCase() + data.complexity_level.slice(1)}
                </span>
              )}
              {data.filing_fee_total > 0 && (
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  ${data.filing_fee_total.toLocaleString()} estimated filing fees
                </span>
              )}
            </div>

            {/* Warnings */}
            {data.warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <div className="space-y-1">
                    {data.warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber-800 dark:text-amber-200">{w}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Phase Breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                Timeline
              </h3>
              <div className="space-y-2">
                {data.phases.map((phase, phaseIdx) => {
                  const isExpanded = expandedPhases.has(phaseIdx);
                  const mandatedCount = phase.tasks.filter((t) => t.is_court_mandated).length;
                  return (
                    <div
                      key={phaseIdx}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => togglePhase(phaseIdx)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`text-[10px] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          >
                            &#9654;
                          </span>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                            {phase.name}
                          </span>
                          <span className="text-xs text-gray-400">
                            {phase.tasks.length} task{phase.tasks.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {mandatedCount > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                              {mandatedCount} mandated
                            </span>
                          )}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="space-y-2 mt-2">
                            {phase.tasks.map((task, taskIdx) => (
                              <div
                                key={taskIdx}
                                className={`rounded-lg p-3 text-sm ${
                                  task.is_court_mandated
                                    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-l-2 border-blue-500'
                                    : 'bg-gray-50/50 dark:bg-gray-800/50'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {task.title}
                                    </span>
                                    {task.is_court_mandated && task.mandate_reference && (
                                      <span className="ml-2 text-[10px] text-blue-600 dark:text-blue-400">
                                        &#9878; {task.mandate_reference}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`text-xs font-medium whitespace-nowrap ${PRIORITY_COLORS[task.priority] || ''}`}>
                                    {task.priority}
                                  </span>
                                </div>

                                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                                  {task.projected_date && (
                                    <span>
                                      Due: <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(task.projected_date)}</span>
                                      {task.due_date_offset_days !== null && (
                                        <span className="ml-1 text-gray-400">(Day {task.due_date_offset_days})</span>
                                      )}
                                    </span>
                                  )}
                                  {task.assignee_role && (
                                    <span>{task.assignee_role.replace(/_/g, ' ')}</span>
                                  )}
                                  {task.estimated_cost !== null && task.estimated_cost > 0 && (
                                    <span className="font-medium">${task.estimated_cost}</span>
                                  )}
                                </div>

                                {task.required_forms.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {task.required_forms.map((f) => (
                                      <span
                                        key={f.form_number}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] ${
                                          f.is_mandatory
                                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                        }`}
                                      >
                                        <span className="font-medium">{f.form_number}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Forms Summary */}
            {allForms.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                  Required Forms ({allForms.length})
                </h3>
                <div className="space-y-1">
                  {mandatoryForms.length > 0 && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{mandatoryForms.length} required:</span>{' '}
                      {mandatoryForms.map((f) => f.form_number).join(', ')}
                    </div>
                  )}
                  {optionalForms.length > 0 && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{optionalForms.length} optional:</span>{' '}
                      {optionalForms.map((f) => f.form_number).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="text-[10px] text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700 pt-3">
              Templates are procedural guides, not legal advice. Verify requirements with your local court.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskTemplatePreviewPanel;
